import React, {ReactNode, useRef, useState} from 'react';
import {Menu, MenuItem} from '@mui/material';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import {MoreHoriz} from '@mui/icons-material';
import {AppIconButton} from 'components/buttons';

export interface MenuItemType {
    label: string;
    onClick?: () => void;
    icon?: ReactNode;
    submenu?: MenuItemType[];
}

interface AppMenuProps {
    menuItems: MenuItemType[];
    icon?: (onClick: () => void) => ReactNode;
    preCustomMenu?: ReactNode;
    postCustomMenu?: ReactNode;
}

export const AppMenu: React.FC<AppMenuProps> = ({menuItems, postCustomMenu, preCustomMenu, icon = undefined}) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null);
    const [submenuItems, setSubmenuItems] = useState<MenuItemType[] | null>(null);

    const open = Boolean(anchorEl);
    const openSubmenu = Boolean(submenuAnchorEl);

    const iconButtonRef = useRef<HTMLButtonElement | null>(null);

    const handleClick = (event?: React.MouseEvent<HTMLButtonElement>) => {
        event?.stopPropagation();
        event?.preventDefault();
        const target = event?.currentTarget || iconButtonRef.current;
        if (target) {
            setAnchorEl(target);
        }
    };

    const handleClose = () => {
        setAnchorEl(null);
        setSubmenuAnchorEl(null);
        setSubmenuItems(null);
    };

    const handleSubmenuOpen = (event: React.MouseEvent<HTMLElement>, items: MenuItemType[]) => {
        setSubmenuItems(items);
        setSubmenuAnchorEl(event.currentTarget);
    };

    return (
        <>
            {/* Main Menu Button */}

            {/*  */}
            {icon ? (
                icon(handleClick) // custom icon calls handleClick with no event
            ) : (
                <AppIconButton onClick={handleClick} ref={iconButtonRef}>
                    <MoreHoriz />
                </AppIconButton>
            )}

            {/* Main Menu */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClick={e => e.stopPropagation()}
                onClose={handleClose}
                BackdropProps={{
                    style: {
                        backgroundColor: 'transparent',
                    },
                }}
            >
                {preCustomMenu}
                {menuItems.map((item, index) => (
                    <MenuItem
                        key={index}
                        onClick={event => {
                            if (item.submenu) {
                                handleSubmenuOpen(event, item.submenu);
                            } else {
                                if (item.onClick) item.onClick();
                                handleClose();
                            }
                        }}
                    >
                        {item.icon && <span style={{marginRight: 8}}>{item.icon}</span>}
                        {item.label}
                        {item.submenu && <ArrowRightIcon style={{marginLeft: 'auto'}} />}
                    </MenuItem>
                ))}
                {postCustomMenu}
            </Menu>

            {/* Submenu */}
            <Menu anchorEl={submenuAnchorEl} open={openSubmenu} onClose={() => setSubmenuAnchorEl(null)}>
                {submenuItems?.map((subItem, subIndex) => (
                    <MenuItem
                        key={subIndex}
                        onClick={() => {
                            if (subItem.onClick) subItem.onClick();
                            handleClose(); // Close the menu when a submenu item is clicked
                        }}
                    >
                        {subItem.icon && <span style={{marginRight: 8}}>{subItem.icon}</span>}
                        {subItem.label}
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export default AppMenu;
